// /academy/glosario — Searchable + category-filtered glossary of trading
// terms. Click a card to expand the full definition + related terms.
import React, { useMemo, useState } from "react";
import { BookOpen, Search, X } from "lucide-react";
import AcademyShell from "../components/academy/AcademyShell";
import { useLanguage } from "../i18n/LanguageContext";
import { getAcademyToolsI18n } from "../i18n/academy-tools";
import {
  getGlosario,
  getGlosarioCategorias,
  findTerminoLang,
  colorForCategoria,
  labelCategoria,
} from "../data/i18n-academy";

const AcademyGlosarioPage = () => {
  const { lang } = useLanguage();
  const T = getAcademyToolsI18n(lang);
  const GLOSARIO = getGlosario(lang);
  const GLOSARIO_CATEGORIAS = getGlosarioCategorias(lang);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("todas");
  const [openId, setOpenId] = useState(null);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return GLOSARIO.filter((t) => {
      if (cat !== "todas" && t.categoria !== cat) return false;
      if (!needle) return true;
      return (
        t.termino.toLowerCase().includes(needle) ||
        t.definicion.toLowerCase().includes(needle)
      );
    }).sort((a, b) => a.termino.localeCompare(b.termino));
  }, [q, cat, GLOSARIO]);

  const opened = openId ? findTerminoLang(openId, lang) : null;

  return (
    <AcademyShell
      testid="academy-glosario"
      eyebrow={`${T.glo_eyebrow_prefix} ${GLOSARIO.length} ${T.glo_terms}`}
      eyebrowIcon={BookOpen}
      title={T.glo_title}
      subtitle={T.glo_subtitle}
      breadcrumbCurrent={T.glo_breadcrumb}
      footHint={`${GLOSARIO.length} ${T.glo_foot_prefix} ${GLOSARIO_CATEGORIAS.length} ${T.glo_categories}`}
    >
      {/* Search */}
      <div className="mb-4 flex items-center gap-2 rounded-xl border border-white/10 bg-black/40 px-3 py-2 backdrop-blur-xl">
        <Search className="h-4 w-4 text-white/40" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={T.glo_search}
          className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/35"
          data-testid="glosario-search"
        />
        {q && (
          <button onClick={() => setQ("")} className="text-white/45 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Category filters */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setCat("todas")}
          data-testid="glosario-cat-todas"
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] transition-all ${
            cat === "todas"
              ? "border-orange-400/60 bg-orange-500/15 text-orange-200 shadow-[0_0_20px_rgba(255,140,0,0.3)]"
              : "border-white/10 bg-white/[0.03] text-white/55 hover:text-white"
          }`}
        >
          {T.glo_all}
        </button>
        {GLOSARIO_CATEGORIAS.map((c) => {
          const active = cat === c;
          return (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`glosario-cat-${c.replace(/\s/g, "-").toLowerCase()}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] transition-all ${
                active
                  ? "border-orange-400/60 bg-orange-500/15 text-orange-200"
                  : "border-white/10 bg-white/[0.03] text-white/55 hover:text-white"
              }`}
            >
              {labelCategoria(c, lang)}
            </button>
          );
        })}
      </div>

      <p className="mb-4 text-xs text-white/40" data-testid="glosario-count">
        <span className="font-bold text-orange-300">{filtered.length}</span> / {GLOSARIO.length}
      </p>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] py-16 text-center text-sm text-white/40">
          {T.glo_empty}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="glosario-grid">
          {filtered.map((t) => (
            <button
              key={t.id}
              onClick={() => setOpenId(t.id)}
              data-testid={`glosario-card-${t.id}`}
              className="group flex flex-col rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left transition-all hover:border-orange-400/40 hover:bg-orange-400/[0.04]"
            >
              <span
                className={`mb-2 inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] ${
                  colorForCategoria(t)
                }`}
              >
                {labelCategoria(t.categoria, lang)}
              </span>
              <h3 className="font-display text-base font-black text-white">{t.termino}</h3>
              <p className="mt-1 line-clamp-3 text-[12px] text-white/55">{t.definicion}</p>
              <span className="mt-auto pt-2 text-[10px] font-bold uppercase tracking-[0.18em] text-orange-300 opacity-0 transition-opacity group-hover:opacity-100">
                {T.glo_view_def}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* MODAL */}
      {opened && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
          onClick={() => setOpenId(null)}
          data-testid="glosario-modal"
        >
          <div
            className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-orange-400/30 bg-[#0a0a0a] p-6 shadow-[0_0_60px_rgba(255,140,0,0.25)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <span
                  className={`mb-2 inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] ${
                    colorForCategoria(opened)
                  }`}
                >
                  {labelCategoria(opened.categoria, lang)}
                </span>
                <h3 className="font-display text-2xl font-black text-white">{opened.termino}</h3>
              </div>
              <button
                onClick={() => setOpenId(null)}
                className="rounded-full border border-white/10 bg-white/[0.04] p-2 text-white/65 hover:text-white"
                data-testid="glosario-modal-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <p className="text-sm leading-relaxed text-white/75">{opened.definicion}</p>

            {opened.ejemplo && (
              <div className="mt-4 rounded-xl border border-orange-400/20 bg-orange-400/[0.05] p-3">
                <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.22em] text-orange-300">
                  {T.glo_example}
                </div>
                <p className="text-[13px] leading-relaxed text-white/70">{opened.ejemplo}</p>
              </div>
            )}

            {opened.relacionados && opened.relacionados.length > 0 && (
              <div className="mt-4">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
                  {T.glo_related}
                </div>
                <div className="flex flex-wrap gap-2">
                  {opened.relacionados
                    .map((id) => findTerminoLang(id, lang))
                    .filter(Boolean)
                    .map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setOpenId(t.id)}
                        className="rounded-full border border-white/15 bg-white/[0.04] px-3 py-1 text-[11px] font-bold text-white/65 transition-all hover:border-orange-400/40 hover:text-orange-200"
                      >
                        {t.termino}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </AcademyShell>
  );
};

export default AcademyGlosarioPage;
